import { useEffect, useRef, useState } from "react";

interface ConfettiProps {
  /**
   * Whether to run the confetti animation
   * @default false
   */
  active?: boolean;
  /**
   * The number of confetti particles
   * @default 50
   */
  particleCount?: number;
  /**
   * Duration of the animation in milliseconds
   * @default 3000
   */
  duration?: number;
  /**
   * Callback when animation completes
   */
  onComplete?: () => void;
}

interface Particle {
  x: number;
  y: number;
  rotation: number;
  rotationSpeed: number;
  size: number;
  color: string;
  velocityX: number;
  velocityY: number;
  opacity: number;
}

const COLORS = [
  "#FFC700",
  "#FF0000",
  "#2E3191",
  "#41BBC7",
  "#FCED00",
  "#FECB00",
  "#FF6B00",
  "#F9D900",
];

export default function Confetti({
  active = false,
  particleCount = 50,
  duration = 3000,
  onComplete,
}: ConfettiProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [particles, setParticles] = useState<Particle[]>([]);
  const animationRef = useRef<number>();

  useEffect(() => {
    if (!active) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Create particles
    const newParticles: Particle[] = Array.from(
      { length: particleCount },
      () => ({
        x: Math.random() * canvas.width,
        y: -20,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        size: Math.random() * 8 + 4,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        velocityX: (Math.random() - 0.5) * 4,
        velocityY: Math.random() * 3 + 2,
        opacity: 1,
      })
    );

    setParticles(newParticles);

    const startTime = Date.now();
    let lastTime = startTime;

    const animate = () => {
      const now = Date.now();
      const deltaTime = (now - lastTime) / 16.67; // Normalize to ~60fps
      lastTime = now;

      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      newParticles.forEach((particle) => {
        // Update position
        particle.x += particle.velocityX * deltaTime;
        particle.y += particle.velocityY * deltaTime;
        particle.rotation += particle.rotationSpeed * deltaTime;

        // Apply gravity
        particle.velocityY += 0.15 * deltaTime;

        // Fade out near the end
        if (progress > 0.7) {
          particle.opacity = Math.max(0, 1 - (progress - 0.7) / 0.3);
        }

        // Draw particle
        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate((particle.rotation * Math.PI) / 180);
        ctx.globalAlpha = particle.opacity;
        ctx.fillStyle = particle.color;
        ctx.fillRect(
          -particle.size / 2,
          -particle.size / 2,
          particle.size,
          particle.size
        );
        ctx.restore();
      });

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setParticles([]);
        onComplete?.();
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [active, particleCount, duration, onComplete]);

  if (!active && particles.length === 0) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 9999,
      }}
    />
  );
}
